/**
 * Catalog download / refresh helpers used by AuthContext and the manual
 * "Refresh catalog" buttons in Scan/Inventory.
 *
 * Scope: the LOGGED-IN USER's products only.
 *   - Employees: their department's catalog
 *     (the backend's /api/products honors `department_id` for filtering).
 *   - Admins: skip — admins always work online. We deliberately do NOT
 *     cache the global 16k-row catalog on every admin's device.
 *
 * The download is fire-and-forget (does not block UI) and tolerates
 * network failure — if it fails, the user continues with whatever
 * catalog is already on the device (possibly nothing).
 */
import api from "./api";
import { saveCatalog, readCatalog } from "./offlineStore";

const FETCH_LIMIT = 1000;

/**
 * Decide whether the current user should have a local catalog. Admins
 * are intentionally excluded — they have no scoped department and we
 * don't want to ship 16k rows to every admin device.
 */
function shouldCacheCatalogFor(user) {
  if (!user || !user.id) return false;
  if (user.role === "admin") return false;
  if (!user.department_id) return false;
  return true;
}

export async function downloadCatalog(user, { force = false } = {}) {
  if (!shouldCacheCatalogFor(user)) return null;
  if (!force) {
    // Skip if we synced within the last hour AND the catalog is non-empty.
    const cached = await readCatalog(user.id);
    if (cached && Array.isArray(cached.products) && cached.products.length > 0) {
      const ageMs = Date.now() - (cached.fetchedAt || 0);
      if (ageMs < 60 * 60 * 1000) return cached;
    }
  }
  try {
    const { data } = await api.get("/products", {
      params: { department_id: user.department_id, limit: FETCH_LIMIT },
    });
    const products = Array.isArray(data) ? data : [];
    await saveCatalog(user.id, products, {
      department_id: user.department_id,
      store_id: user.store_id || null,
    });
    return { fetchedAt: Date.now(), products };
  } catch (_) {
    // Best-effort: return whatever is already on disk so the caller can
    // continue offline.
    return readCatalog(user.id);
  }
}

export { readCatalog };
